import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
    title?: string;
    description?: string;
    keywords?: string[];
    image?: string;
    url?: string;
    type?: 'website' | 'article' | 'product';
    author?: string;
    publishedTime?: string;
    modifiedTime?: string;
    section?: string;
    tags?: string[];
}

const SEOHead: React.FC<SEOHeadProps> = ({
    title = "Quicklish: Quick English Learning",
    description = "Master English with AI-powered conversations, grammar practice, and interactive lessons. Free online English learning platform with personalized tutoring.",
    keywords = [
        "learn English online",
        "English grammar practice",
        "AI English tutor",
        "English conversation practice",
        "free English lessons",
        "English learning app",
        "improve English speaking",
        "English vocabulary builder",
        "online English course",
        "English grammar exercises"
    ],
    image = "https://myenglishgurukul.com/og-image.jpg",
    url = "https://myenglishgurukul.com",
    type = "website",
    author = "Quicklish",
    publishedTime,
    modifiedTime,
    section,
    tags = ["English Learning", "AI Education", "Grammar Practice", "Language Learning"]
}) => {
    const fullTitle = title.includes("Quicklish") ? title : `${title} | Quicklish`;
    const fullUrl = url.startsWith('http') ? url : `https://myenglishgurukul.com${url}`;
    const fullImage = image.startsWith('http') ? image : `https://myenglishgurukul.com${image}`;

    return (
        <Helmet>
            {/* Basic Meta Tags */}
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            <meta name="keywords" content={keywords.join(', ')} />
            <meta name="author" content={author} />
            <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
            <meta name="googlebot" content="index, follow" />

            {/* Canonical URL */}
            <link rel="canonical" href={fullUrl} />

            {/* Open Graph Meta Tags */}
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={fullUrl} />
            <meta property="og:type" content={type} />
            <meta property="og:image" content={fullImage} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content={fullTitle} />
            <meta property="og:site_name" content="Quicklish" />
            <meta property="og:locale" content="en_US" />

            {/* Twitter Card Meta Tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={fullImage} />
            <meta name="twitter:image:alt" content={fullTitle} />
            <meta name="twitter:site" content="@myenglishgurukul" />
            <meta name="twitter:creator" content="@myenglishgurukul" />

            {/* Article Specific Meta Tags */}
            {type === 'article' && (
                <>
                    {publishedTime && <meta property="article:published_time" content={publishedTime} />}
                    {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
                    {author && <meta property="article:author" content={author} />}
                    {section && <meta property="article:section" content={section} />}
                    {tags.map((tag, index) => (
                        <meta key={index} property="article:tag" content={tag} />
                    ))}
                </>
            )}

            {/* Additional SEO Meta Tags */}
            <meta name="application-name" content="Quicklish" />
            <meta name="apple-mobile-web-app-title" content="Quicklish" />
            <meta name="theme-color" content="#3B82F6" />
            <meta name="msapplication-TileColor" content="#3B82F6" />

            {/* Language and Region */}
            <meta property="og:locale:alternate" content="hi_IN" />
            <meta property="og:locale:alternate" content="mr_IN" />
            <meta property="og:locale:alternate" content="gu_IN" />

            {/* Structured Data for Educational Application */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "EducationalApplication",
                    "name": "Quicklish",
                    "description": description,
                    "url": fullUrl,
                    "applicationCategory": "EducationalApplication",
                    "operatingSystem": "Web Browser",
                    "offers": {
                        "@type": "Offer",
                        "price": "0",
                        "priceCurrency": "USD",
                        "availability": "https://schema.org/InStock"
                    },
                    "provider": {
                        "@type": "Organization",
                        "name": "Quicklish",
                        "url": "https://myenglishgurukul.com",
                        "logo": "https://myenglishgurukul.com/logo.png"
                    },
                    "aggregateRating": {
                        "@type": "AggregateRating",
                        "ratingValue": "4.8",
                        "ratingCount": "1250",
                        "bestRating": "5",
                        "worstRating": "1"
                    },
                    "author": {
                        "@type": "Organization",
                        "name": "Quicklish"
                    },
                    "audience": {
                        "@type": "Audience",
                        "audienceType": "English Language Learners"
                    },
                    "educationalLevel": ["Beginner", "Intermediate", "Advanced"],
                    "inLanguage": ["en", "hi", "mr", "gu"],
                    "learningResourceType": ["Interactive", "Practice", "Assessment"],
                    "teaches": "English Language",
                    "educationalUse": "Self Study"
                })}
            </script>

            {/* Breadcrumb Structured Data */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "BreadcrumbList",
                    "itemListElement": [
                        {
                            "@type": "ListItem",
                            "position": 1,
                            "name": "Home",
                            "item": "https://myenglishgurukul.com"
                        },
                        {
                            "@type": "ListItem",
                            "position": 2,
                            "name": title,
                            "item": fullUrl
                        }
                    ]
                })}
            </script>

            {/* Organization Structured Data */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Organization",
                    "name": "Quicklish",
                    "url": "https://myenglishgurukul.com",
                    "logo": "https://myenglishgurukul.com/logo.png",
                    "description": "AI-powered English learning platform",
                    "foundingDate": "2024",
                    "sameAs": [
                        "https://twitter.com/myenglishgurukul",
                        "https://facebook.com/myenglishgurukul",
                        "https://linkedin.com/company/myenglishgurukul"
                    ],
                    "contactPoint": {
                        "@type": "ContactPoint",
                        "contactType": "customer service",
                        "email": "support@myenglishgurukul.com"
                    }
                })}
            </script>
        </Helmet>
    );
};

export default SEOHead; 